/**
 * Shared display utilities for sample/analysis types.
 * Used across Batches, Dashboard, HybridNorms, and PlantPerformance features.
 */
import { drisAnalysisColors } from '../theme/colors';

/**
 * Returns a badge descriptor (label + color) for a batch's sample types.
 * @param types - Array of uppercase sample type strings (e.g. ['SAP', 'TISSUE'])
 * @returns Badge config or null if no recognizable types
 */
export function getSampleTypeBadge(
  types: string[]
): { label: string; color: string } | null {
  const upperTypes = types.map(t => t.toUpperCase());
  const hasSap = upperTypes.includes('SAP');
  const hasTissue = upperTypes.includes('TISSUE');
  const hasSoil = upperTypes.includes('SOIL');

  if (hasSoil) return { label: 'Soil', color: drisAnalysisColors.soil };
  if (hasSap && hasTissue) return { label: 'SAP & Tissue', color: drisAnalysisColors.sap };
  if (hasSap) return { label: 'SAP Only', color: drisAnalysisColors.sap };
  if (hasTissue) return { label: 'Tissue Only', color: drisAnalysisColors.tissue };
  return null;
}

/** Value used by the Sample Type list filter (Workflow + Batches lists). Empty = no filter. */
export type SampleTypeFilterValue = '' | 'Leaf' | 'Soil';

/**
 * Options for the Sample Type list-filter dropdown. "Leaf" groups Sap + Tissue (the two plant/leaf
 * analyses) vs Soil — matching how the app already groups sample types elsewhere.
 */
export const SAMPLE_TYPE_FILTER_OPTIONS: { value: 'Leaf' | 'Soil'; label: string }[] = [
  { value: 'Leaf', label: 'Leaf (Sap & Tissue)' },
  { value: 'Soil', label: 'Soil' },
];

/**
 * Returns MUI sx-compatible styles for a sample result type Chip.
 * @param resultType - The result type string (e.g. 'sap', 'tissue', 'soil')
 * @returns Style object for Chip sx prop, or undefined if no type
 */
export function getResultTypeChipSx(
  resultType?: string
): { backgroundColor: string; color: string; fontWeight: number } | undefined {
  if (!resultType) return undefined;
  switch (resultType.toLowerCase()) {
    case 'sap':
      return { backgroundColor: drisAnalysisColors.sap, color: '#fff', fontWeight: 600 };
    case 'tissue':
      return { backgroundColor: drisAnalysisColors.tissue, color: '#fff', fontWeight: 600 };
    case 'soil':
      return { backgroundColor: drisAnalysisColors.soil, color: '#fff', fontWeight: 600 };
    default:
      return { backgroundColor: '#9e9e9e', color: '#fff', fontWeight: 600 };
  }
}
